import api from '../utils/api';
import type {
  Scadenza,
  CreateScadenzaDto,
  UpdateScadenzaDto,
  ScadenzaFilters,
  ScadenzaStats,
  CompletaScadenzaDto,
  StoricoRinnoviResponse
} from '../types/scadenza.types';
import type { PaginatedResponse, QueryParams } from '../types/common.types';

export const scadenzeService = {
  // Get all scadenze with pagination and filters
  getAll: async (params: QueryParams & ScadenzaFilters): Promise<PaginatedResponse<Scadenza>> => {
    const response = await api.get('/scadenze', { params });
    return response.data;
  },

  // Get scadenza by ID
  getById: async (id: string): Promise<Scadenza> => {
    const response = await api.get(`/scadenze/${id}`);
    return response.data;
  },

  // Create new scadenza
  create: async (data: CreateScadenzaDto): Promise<Scadenza> => {
    const response = await api.post('/scadenze', data);
    return response.data;
  },

  // Update scadenza
  update: async (id: string, data: UpdateScadenzaDto): Promise<Scadenza> => {
    const response = await api.put(`/scadenze/${id}`, data);
    return response.data;
  },

  // Delete scadenza
  delete: async (id: string): Promise<void> => {
    await api.delete(`/scadenze/${id}`);
  },

  // Complete scadenza
  complete: async (id: string, data?: CompletaScadenzaDto): Promise<Scadenza> => {
    const response = await api.patch(`/scadenze/${id}/completa`, data);
    return response.data;
  },

  // Cancel scadenza
  cancel: async (id: string, motivo?: string): Promise<Scadenza> => {
    const response = await api.patch(`/scadenze/${id}/annulla`, { motivo });
    return response.data;
  },

  // Bulk complete scadenze
  bulkComplete: async (ids: string[]): Promise<void> => {
    await api.post('/scadenze/bulk/completa', { ids });
  },

  // Bulk cancel scadenze
  bulkCancel: async (ids: string[], motivo?: string): Promise<void> => {
    await api.post('/scadenze/bulk/annulla', { ids, motivo });
  },

  // Bulk delete scadenze
  bulkDelete: async (ids: string[]): Promise<void> => {
    await api.post('/scadenze/bulk/delete', { ids });
  },

  // Get scadenze statistics
  getStats: async (filters?: ScadenzaFilters): Promise<ScadenzaStats> => {
    const response = await api.get('/scadenze/stats', { params: filters });
    return response.data;
  },

  // Get scadenze in scadenza (expiring soon)
  getInScadenza: async (giorni: number = 7): Promise<Scadenza[]> => {
    const response = await api.get('/scadenze/in-scadenza', { params: { giorni } });
    return response.data;
  },

  // Get scadenze del mese corrente o specificato
  getScadenzeDelMese: async (mese?: number, anno?: number): Promise<Scadenza[]> => {
    const params: { mese?: number; anno?: number } = {};
    if (mese !== undefined) params.mese = mese;
    if (anno !== undefined) params.anno = anno;
    
    const response = await api.get('/scadenze/mese-corrente', { params });
    return response.data.data; // Estrae l'array dalla proprietà data
  },

  // Export scadenze to CSV
  exportToCsv: async (filters?: ScadenzaFilters): Promise<Blob> => {
    const response = await api.get('/scadenze/export/csv', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  // Upload file for scadenza
  uploadFile: async (id: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/scadenze/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.filePath;
  },

  // Delete file from scadenza
  deleteFile: async (id: string, filePath: string): Promise<void> => {
    await api.delete(`/scadenze/${id}/files`, { data: { filePath } });
  },

  // Get storico rinnovi per una scadenza
  getStoricoRinnovi: async (id: string): Promise<StoricoRinnoviResponse> => {
    const response = await api.get(`/scadenze/${id}/storico-rinnovi`);
    return response.data;
  }
};

export default scadenzeService;
